/**
 * Externally driven hover hint — the dark `.tip__panel` surface without a trigger of its own.
 *
 * `TooltipPopover` owns its open state through a focusable button, which the Posts column headers
 * cannot use: those cells already own pointer events for drag-reorder and click-to-sort. Here the
 * caller decides when the hint is visible and which element it hangs off, so the header's pointer
 * handling stays untouched. Portaled and anchored like every other overlay (§12).
 */

import { useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '../lib/anchored';

export type HoverTipProps = {
  open: boolean;
  /** Element the hint is anchored under. */
  anchorRef: RefObject<HTMLElement | null>;
  text: string;
};

export function HoverTip({ open, anchorRef, text }: HoverTipProps) {
  const panelRef = useRef<HTMLSpanElement>(null);
  const style = useAnchoredPosition(open, anchorRef, panelRef, { align: 'center', gapPx: 6 });

  if (!open) return null;
  return createPortal(
    <span className="tip__panel" ref={panelRef} style={style ?? undefined} role="tooltip">
      {text}
    </span>,
    document.body,
  );
}

export default HoverTip;
